import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Brain, Search, Check } from 'lucide-react';

interface ApproachSelectionProps {
  onSelect: (approach: 'prediction' | 'similarity') => void;
  selected?: 'prediction' | 'similarity';
  disabled?: boolean;
}

export const ApproachSelection: React.FC<ApproachSelectionProps> = ({ 
  onSelect, 
  selected, 
  disabled 
}) => {
  return (
    <div className="space-y-4">
      <p className="text-slate-400">
        Choose your ranking approach:
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <Card 
          className={`cursor-pointer transition-all border-slate-600 bg-slate-800 ${
            selected === 'prediction' ? 'ring-2 ring-purple-500 bg-purple-500/10' : 'hover:shadow-md hover:border-slate-500'
          }`}
          onClick={() => !disabled && onSelect('prediction')}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Brain className="w-5 h-5" />
              Prediction Score
              {selected === 'prediction' && (
                <Badge variant="outline" className="text-green-400 border-green-400 ml-auto">
                  <Check className="w-3 h-3 mr-1" />
                  Selected
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 text-slate-400">
              <li>• Requires positive and negative labels</li>
            </ul>
            {!disabled && (
              <Button 
                className="w-full mt-4" 
                variant={selected === 'prediction' ? 'default' : 'outline'}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect('prediction');
                }}
              >
                {selected === 'prediction' ? 'Selected' : 'Select Prediction'}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all border-slate-600 bg-slate-800 ${
            selected === 'similarity' ? 'ring-2 ring-purple-500 bg-purple-500/10' : 'hover:shadow-md hover:border-slate-500'
          }`}
          onClick={() => !disabled && onSelect('similarity')}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Search className="w-5 h-5" />
              Similarity Search
              {selected === 'similarity' && (
                <Badge variant="outline" className="text-green-400 border-green-400 ml-auto">
                  <Check className="w-3 h-3 mr-1" />
                  Selected
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 text-slate-400">
              <li>• No negative labels required</li>
            </ul>
            {!disabled && (
              <Button 
                className="w-full mt-4" 
                variant={selected === 'similarity' ? 'default' : 'outline'}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect('similarity');
                }}
              >
                {selected === 'similarity' ? 'Selected' : 'Select Similarity'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}; 